/**
 * Central schema for the 2026 all-claims-v2 526 form — the single source of truth
 * for the form's data shape. Merges the published 21-526EZ-ALLCLAIMS-V2 schema
 * from vets-json-schema with local forms-system pattern schemas for fields not
 * yet defined there. Local properties/definitions take precedence when a key
 * exists in both, since their shape is what the page uiSchemas are built against.
 *
 * Page files import the field/item schemas they need from here rather than
 * defining schema inline, keeping one canonical definition per field.
 */
import vetsJsonSchema from 'vets-json-schema/dist/21-526EZ-ALLCLAIMS-V2-schema.json';

import {
  addressSchema,
  arrayBuilderYesNoSchema,
  currentOrPastDateRangeSchema,
  currentOrPastMonthYearDateSchema,
  fileInputMultipleSchema,
  radioSchema,
  textSchema,
  yesNoSchema,
} from 'platform/forms-system/src/js/web-component-patterns';

import {
  POW_ERA_KEYS,
  PROVIDER_TYPE_KEYS,
  USA,
  VA_FACILITY_TYPE_KEYS,
} from '../constants';

// --- Reusable object/item schemas (exported for array-builder item pages) ---

// Field names match the all-claims `vaTreatmentFacilities` items because the
// shared reconciler (all-claims/utils/reconcile-supporting-evidence.js) reads
// them to keep `treatedDisabilityNames` in sync when conditions are renamed
// or removed. Rename them only together with that reconciler.
//
// Three fields are built locally on purpose:
//   - `facilityType` uses VA_FACILITY_TYPE_KEYS, derived from
//     VA_FACILITY_TYPE_LABELS, so every radio option is guaranteed a label and
//     the on-screen option order stays with the copy. The published enum
//     carries no extra constraints; a spec asserts the two lists match.
//   - `treatedDisabilityNames` is a checkbox object here; the submit
//     transformer turns it into the schema's array of names.
//   - `treatmentLocation0781Related` uses the yes/no schema, which produces
//     the boolean the published schema wants.
const publishedVaFacility =
  vetsJsonSchema.properties.vaTreatmentFacilities.items;
const publishedVaFacilityAddress =
  vetsJsonSchema.definitions.vaTreatmentCenterAddress;

export const vaTreatmentFacilityItemSchema = {
  type: 'object',
  properties: {
    treatmentCenterName: publishedVaFacility.properties.treatmentCenterName,
    treatmentCenterAddress: {
      type: publishedVaFacilityAddress.type,
      required: ['country', 'city'],
      properties: {
        // Inlined because the published definition uses `$ref`s, which
        // array-builder item pages don't resolve.
        country: { ...vetsJsonSchema.definitions.country, default: USA },
        state: vetsJsonSchema.definitions.state,
        city: publishedVaFacilityAddress.properties.city,
      },
    },
    facilityType: radioSchema(VA_FACILITY_TYPE_KEYS),
    facilityTypeOther: publishedVaFacility.properties.facilityTypeOther,
    // Keyed by sippableId. Options are built at runtime from the claimed
    // conditions by the conditions page's updateSchema.
    treatedDisabilityNames: { type: 'object', properties: {} },
    treatmentDateRange: {
      type: 'object',
      properties: {
        from: currentOrPastMonthYearDateSchema,
      },
    },
    treatmentLocation0781Related: yesNoSchema,
  },
  required: publishedVaFacility.required,
};

// Field names match the v1 all-claims `providerFacility` items (see
// all-claims/pages/privateMedicalRecordsRelease.js) so the shared reconciler
// (all-claims/utils/reconcile-supporting-evidence.js) keeps
// `treatedDisabilityNames` in sync when conditions are renamed or removed.
export const providerFacilityItemSchema = {
  type: 'object',
  properties: {
    providerFacilityName: textSchema,
    providerType: radioSchema(PROVIDER_TYPE_KEYS),
    providerTypeOther: textSchema,
    providerFacilityAddress: addressSchema({
      omit: ['isMilitary', 'street3'],
    }),
    // Keyed by sippableId. Options are built at runtime from the claimed
    // conditions by the conditions page's updateSchema.
    treatedDisabilityNames: { type: 'object', properties: {} },
    // Full date, not month/year-only like VA's facility date
    treatmentDateRange: currentOrPastDateRangeSchema,
  },
  required: ['providerFacilityName', 'providerFacilityAddress'],
};

// --- Full form schema (definitions + properties) ---

const fullSchema = {
  $schema: 'http://json-schema.org/draft-04/schema#',
  title: 'Application for Disability Compensation (21-526EZ, 2026)',
  type: 'object',
  definitions: {
    ...vetsJsonSchema.definitions,
    vaTreatmentFacilityItem: vaTreatmentFacilityItemSchema,
    providerFacilityItem: providerFacilityItemSchema,
  },
  properties: {
    ...vetsJsonSchema.properties,
    isCurrentlyHomelessOrIsAtRiskOfBecomingHomeless: yesNoSchema,
    isTerminallyIll: yesNoSchema,
    // Chapter 2 — Conditions
    powStatus: yesNoSchema,
    // the pages for these inputs are displayed only when powStatus is "yes"
    powEraCampaign: radioSchema(POW_ERA_KEYS),
    powConfinement: currentOrPastDateRangeSchema,

    // Chapter 4 — Supporting evidence
    vaTreatmentFacilities: {
      type: 'array',
      maxItems: 100,
      items: vaTreatmentFacilityItemSchema,
    },
    'view:hasVaTreatmentFacilities': arrayBuilderYesNoSchema,
    'view:limitedConsent': { type: 'boolean' },
    limitedConsent: textSchema,
    patient4142Acknowledgement: { type: 'boolean' },
    providerFacility: {
      type: 'array',
      maxItems: 100,
      items: providerFacilityItemSchema,
    },
    'view:hasProviderFacilities': arrayBuilderYesNoSchema,
    additionalDocuments: fileInputMultipleSchema(),
  },
};

export const { definitions } = fullSchema;

export default fullSchema;
